import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Mail, Phone, Calendar, Check, X, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadsPage = () => {
  const { studio } = useOutletContext();
  const { getAuthHeader } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads`, { headers: getAuthHeader() });
      setLeads(response.data);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (leadId, status) => {
    try {
      await axios.put(`${API}/leads/${leadId}/status?status=${status}`, {}, { headers: getAuthHeader() });
      toast.success('Lead status updated');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const filteredLeads = leads.filter(lead => {
    if (filter === 'all') return true;
    return lead.status === filter;
  });

  const statusColors = {
    new: 'bg-[#FFFFFF]/20 text-[#FFFFFF]',
    contacted: 'bg-blue-500/20 text-blue-400',
    converted: 'bg-green-500/20 text-green-400',
    closed: 'bg-white/10 text-white/50'
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="leads-page"
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Leads</h1>
          <p className="text-white/60">{leads.length} total leads</p>
        </div>
        
        {/* Filter */}
        <div className="flex gap-2">
          {['all', 'new', 'contacted', 'converted', 'closed'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-sm text-sm font-medium capitalize transition-all ${
                filter === status 
                  ? 'bg-[#FFFFFF] text-black' 
                  : 'bg-[#171717] text-white/60 hover:bg-[#1f1f1f]'
              }`}
              data-testid={`filter-${status}`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-24 bg-[#0F0F0F] rounded-sm animate-pulse"></div>
          ))}
        </div>
      ) : filteredLeads.length === 0 ? (
        <div className="text-center py-16 bg-[#0F0F0F] border border-white/10 rounded-sm">
          <p className="text-white/40">No leads found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredLeads.map((lead) => (
            <div 
              key={lead.id}
              className="bg-[#0F0F0F] border border-white/10 rounded-sm p-6"
              data-testid={`lead-${lead.id}`}
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-lg">{lead.name}</h3>
                    <span className={`text-xs px-2 py-1 rounded capitalize ${statusColors[lead.status]}`}>
                      {lead.status}
                    </span>
                  </div>
                  
                  <div className="flex flex-wrap gap-4 text-sm text-white/60 mb-3">
                    <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-white">
                      <Mail size={14} /> {lead.email}
                    </a>
                    {lead.phone && (
                      <a href={`tel:${lead.phone}`} className="flex items-center gap-1 hover:text-white">
                        <Phone size={14} /> {lead.phone}
                      </a>
                    )}
                    <span className="flex items-center gap-1">
                      <Calendar size={14} /> {formatDate(lead.created_at)}
                    </span>
                  </div>

                  {lead.message && (
                    <p className="text-white/50 text-sm bg-[#171717] p-3 rounded-sm">
                      "{lead.message}"
                    </p>
                  )}

                  {lead.render_id && (
                    <p className="text-[#FFFFFF] text-xs mt-2">
                      Has render preview attached
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  {lead.status === 'new' && (
                    <Button
                      onClick={() => handleStatusChange(lead.id, 'contacted')}
                      variant="outline"
                      className="border-white/20 text-sm"
                    >
                      <Clock size={14} className="mr-1" /> Mark Contacted
                    </Button>
                  )}
                  {lead.status === 'contacted' && (
                    <>
                      <Button
                        onClick={() => handleStatusChange(lead.id, 'converted')}
                        className="bg-green-500 text-white hover:bg-green-600 text-sm"
                      >
                        <Check size={14} className="mr-1" /> Converted
                      </Button>
                      <Button
                        onClick={() => handleStatusChange(lead.id, 'closed')}
                        variant="outline"
                        className="border-white/20 text-sm"
                      >
                        <X size={14} className="mr-1" /> Close
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default LeadsPage;
